import { useLocalePath } from "../../hooks/useLocalePath";
import { PlayerDailyChange } from "../../types/leaderboard";
import {
  formatHelsinkiDayOption,
  formatHelsinkiWeekOption,
} from "../../utils/leaderboard/helsinki-time";
import { formatScoreDelta } from "../../utils/leaderboard/rank";
import { teamLogoUrl } from "../../utils/teamLogo";
import { getPlayerProfilePath } from "../../utils/playerProfilePath";
import { TeamLogo } from "../shared/TeamLogo";
import { OpggRankCell } from "./OpggRankCell";
import { PlayerExternalLinks } from "./PlayerExternalLinks";
import { PlayerNameDisplay } from "./PlayerNameDisplay";
import { PlayerRoleBadge } from "./PlayerRoleBadge";
import { ProfileIcon } from "./ProfileIcon";
import { useDailyMovers } from "./hooks/useDailyMovers";

interface TeamMeta {
  teamName: string;
  fullName: string;
  logoUrl: string;
}

const PERIOD_TAB = "fh-ghost-btn rounded-lg px-[0.85rem] py-[0.45rem] text-[0.84rem]";

const DAY_PICKER_SELECT =
  "cursor-pointer appearance-none rounded-lg border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] py-2 pl-3 pr-8 text-[0.9rem] font-semibold text-[#e8e8ef] [background-image:var(--select-caret)] [background-position:right_0.7rem_center] [background-repeat:no-repeat]";

const PICKER_LABEL = "text-[0.78rem] font-semibold uppercase tracking-[0.04em] text-[#8b8b9a]";

const EMPTY_STATE = "p-6 text-center text-[0.9rem] text-[#8b8b9a]";

interface DailyMoverRowProps {
  position: number;
  entry: PlayerDailyChange;
  teamName: string;
  teamId: string;
  teamLogoAlt: string;
  direction: "up" | "down";
  profileLabel: string;
  rankSiteLabel: (site: string) => string;
}

const DailyMoverRow = ({
  position,
  entry,
  teamName,
  teamId,
  teamLogoAlt,
  direction,
  profileLabel,
  rankSiteLabel,
}: DailyMoverRowProps) => {
  const { goTo } = useLocalePath();
  const profilePath = getPlayerProfilePath(entry.gameName);
  const openPlayer = () => {
    goTo(profilePath);
  };

  return (
    <div
      className="group grid cursor-pointer grid-cols-[2rem_40px_minmax(0,1fr)_auto_auto] items-center gap-[0.85rem] border-b border-[var(--surface-card-border)] pb-[0.85rem] pl-5 pr-2 pt-[0.85rem] text-inherit no-underline outline-none transition-[background,box-shadow] duration-150 ease-in-out last:border-b-0 hover:bg-[rgba(123,147,255,0.08)] hover:shadow-[inset_3px_0_0_#7b93ff] focus-visible:bg-[rgba(123,147,255,0.08)] focus-visible:shadow-[inset_3px_0_0_#7b93ff] max-[900px]:grid-cols-[1.75rem_36px_minmax(0,1fr)_auto] max-[900px]:grid-rows-[auto_auto_auto] max-[900px]:items-start"
      role="link"
      tabIndex={0}
      aria-label={profileLabel.replace("{player}", entry.gameName)}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest("a")) return;
        openPlayer();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        if ((event.target as HTMLElement).closest("a")) return;
        event.preventDefault();
        openPlayer();
      }}
    >
      <span className="text-center text-[0.95rem] font-bold text-[#8b8b9a] max-[900px]:col-start-1 max-[900px]:row-start-1 max-[900px]:self-center">
        {position}
      </span>
      <ProfileIcon
        size={40}
        className="max-[900px]:col-start-2 max-[900px]:row-start-1 max-[900px]:h-9 max-[900px]:w-9 max-[900px]:self-center"
      />
      <div className="min-w-0 flex-1 max-[900px]:contents">
        <div className="flex min-w-0 flex-col items-start gap-[0.2rem] max-[900px]:col-start-3 max-[900px]:row-start-1 max-[900px]:max-w-full">
          <div className="flex min-w-0 flex-wrap items-center gap-[0.4rem]">
            <PlayerNameDisplay
              displayName={entry.displayName}
              gameName={entry.gameName}
              tagLine={entry.tagLine}
            />
            <PlayerRoleBadge role={entry.role} />
          </div>
          <span className="inline-flex items-center gap-[0.35rem] text-[0.78rem] text-[#8b8b9a] max-[900px]:max-w-full">
            <TeamLogo teamId={teamId} alt={teamLogoAlt} className="h-4 w-4 rounded object-cover" />
            <span className="max-[900px]:overflow-hidden max-[900px]:text-ellipsis max-[900px]:whitespace-nowrap">
              {teamName}
            </span>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-[0.65rem] [&_.opgg-rank-cell]:w-[8.75rem] max-[900px]:col-span-full max-[900px]:row-start-2 max-[900px]:w-full max-[900px]:max-w-full max-[900px]:flex-nowrap max-[900px]:gap-2 max-[900px]:overflow-x-auto max-[900px]:pb-[0.1rem] max-[900px]:[-webkit-overflow-scrolling:touch] max-[900px]:[scrollbar-width:thin] max-[900px]:[&_.opgg-rank-cell]:w-auto max-[900px]:[&_.opgg-rank-cell]:min-w-[7rem] max-[900px]:[&_.opgg-rank-cell]:shrink-0">
          <OpggRankCell
            tier={entry.previousTier}
            rank={entry.previousRank}
            leaguePoints={entry.previousLeaguePoints}
            emblemSize={36}
            muted
          />
          <span
            className="text-base font-semibold leading-none text-[#8b8b9a] max-[900px]:shrink-0"
            aria-hidden="true"
          >
            →
          </span>
          <OpggRankCell
            tier={entry.tier}
            rank={entry.rank}
            leaguePoints={entry.leaguePoints}
            emblemSize={36}
          />
        </div>
      </div>
      <span
        className={`self-center whitespace-nowrap text-[0.92rem] font-bold max-[900px]:col-start-4 max-[900px]:row-start-1 max-[900px]:self-start max-[900px]:justify-self-end max-[900px]:text-[0.85rem] ${
          direction === "up" ? "text-[#34d399]" : "text-[#fb7185]"
        }`}
      >
        {formatScoreDelta(entry.change)}
      </span>
      <PlayerExternalLinks
        displayName={entry.displayName}
        tagLine={entry.tagLine}
        gameName={entry.gameName}
        rankSiteLabel={rankSiteLabel}
        size="sm"
        className="pr-3 max-[900px]:col-[2/-1] max-[900px]:row-start-1 max-[900px]:justify-self-end max-[900px]:pr-0"
      />
    </div>
  );
};

interface DailyMoversPanelProps {
  title: string;
  entries: PlayerDailyChange[];
  direction: "up" | "down";
  teamMetaById: Record<string, TeamMeta>;
  emptyMessage: string;
  teamLogoAlt: (teamName: string) => string;
  profileLabel: string;
  rankSiteLabel: (site: string) => string;
}

const DailyMoversPanel = ({
  title,
  entries,
  direction,
  teamMetaById,
  emptyMessage,
  teamLogoAlt,
  profileLabel,
  rankSiteLabel,
}: DailyMoversPanelProps) => (
  <section className="surface-card overflow-hidden max-[900px]:overflow-visible">
    <div
      className={`border-b px-5 pb-3 pt-4 ${
        direction === "up" ? "border-[rgba(52,211,153,0.25)]" : "border-[rgba(251,113,133,0.25)]"
      }`}
    >
      <h2 className="m-0 text-base font-semibold normal-case tracking-normal text-[#e8e8ef]">
        {title}
      </h2>
    </div>
    <div className="py-2">
      {entries.length ? (
        <div className="flex flex-col">
          {entries.map((entry, index) => {
            const meta = entry.teamId in teamMetaById ? teamMetaById[entry.teamId] : undefined;
            const name = meta ? meta.fullName || meta.teamName : entry.teamId;
            return (
              <DailyMoverRow
                key={`${entry.gameName}#${entry.tagLine}`}
                position={index + 1}
                entry={entry}
                teamName={name}
                teamId={entry.teamId}
                teamLogoAlt={teamLogoAlt(name)}
                direction={direction}
                profileLabel={profileLabel}
                rankSiteLabel={rankSiteLabel}
              />
            );
          })}
        </div>
      ) : (
        <div className={EMPTY_STATE}>{emptyMessage}</div>
      )}
    </div>
  </section>
);

interface DailyMoversProps {
  teamMetaById?: Record<string, TeamMeta>;
}

export const DailyMovers = ({ teamMetaById: teamMetaByIdProp }: DailyMoversProps) => {
  const {
    strings,
    period,
    selectPeriod,
    periodOptions,
    availableDays,
    availableWeeks,
    selectedDay,
    selectDay,
    selectedWeek,
    selectWeek,
    movers,
    error,
    isPending,
    teamLogoAlt,
    emptyMessage,
    gainersTitle,
    losersTitle,
    liveSubtitle,
  } = useDailyMovers();

  const teamMetaById = teamMetaByIdProp ?? {};

  return (
    <div className={isPending ? "pointer-events-none opacity-[0.72]" : undefined}>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4 max-[900px]:flex-col max-[900px]:items-stretch">
        <div className="flex flex-col gap-2">
          <div
            className="inline-flex rounded-[10px] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] p-[0.2rem]"
            role="group"
            aria-label={strings.LEADERBOARD.PERIOD_GROUP}
          >
            {periodOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`${PERIOD_TAB}${
                  period === option.id ? " fh-ghost-btn--active-accent" : ""
                }`}
                onClick={() => {
                  selectPeriod(option.id);
                }}
                aria-pressed={period === option.id}
              >
                {option.label}
              </button>
            ))}
          </div>

          {liveSubtitle ? (
            <p className="m-0 flex items-center gap-2 text-[0.85rem] text-[#8b8b9a]">
              {movers?.live ? (
                <span className="inline-flex items-center rounded-full border border-[rgba(34,197,94,0.35)] bg-[rgba(34,197,94,0.15)] px-[0.45rem] py-[0.1rem] text-[0.68rem] font-bold uppercase tracking-[0.04em] text-[#86efac]">
                  {strings.LEADERBOARD.DAILY_LIVE_BADGE}
                </span>
              ) : null}
              {liveSubtitle}
            </p>
          ) : null}
        </div>

        {period === "daily" ? (
          <label className="inline-flex flex-col gap-[0.35rem]">
            <span className={PICKER_LABEL}>{strings.LEADERBOARD.PICKER_DAY}</span>
            <select
              className={DAY_PICKER_SELECT}
              value={selectedDay ?? ""}
              onChange={(event) => {
                selectDay(event.target.value || null);
              }}
              disabled={!availableDays.length || isPending}
            >
              {!availableDays.length ? (
                <option value="">{strings.LEADERBOARD.NO_DAYS_YET}</option>
              ) : null}
              {availableDays.map((dayKey) => (
                <option key={dayKey} value={dayKey}>
                  {formatHelsinkiDayOption(dayKey, strings.LEADERBOARD.DAILY_TODAY)}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="inline-flex flex-col gap-[0.35rem]">
            <span className={PICKER_LABEL}>{strings.LEADERBOARD.PICKER_WEEK}</span>
            <select
              className={DAY_PICKER_SELECT}
              value={selectedWeek ?? ""}
              onChange={(event) => {
                selectWeek(event.target.value || null);
              }}
              disabled={!availableWeeks.length || isPending}
            >
              {!availableWeeks.length ? (
                <option value="">{strings.LEADERBOARD.NO_WEEKS_YET}</option>
              ) : null}
              {availableWeeks.map((weekKey) => (
                <option key={weekKey} value={weekKey}>
                  {formatHelsinkiWeekOption(weekKey, strings.LEADERBOARD.WEEKLY_THIS_WEEK)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {error ? <div className={EMPTY_STATE}>{error}</div> : null}

      <div className="grid grid-cols-2 items-start gap-5 max-[900px]:grid-cols-1">
        <DailyMoversPanel
          title={gainersTitle}
          entries={movers?.gainers ?? []}
          direction="up"
          teamMetaById={teamMetaById}
          emptyMessage={emptyMessage}
          teamLogoAlt={teamLogoAlt}
          profileLabel={strings.LEADERBOARD.VIEW_PLAYER_PROFILE}
          rankSiteLabel={(site) =>
            strings.LEADERBOARD.VIEW_PLAYER_RANK_SITE.replace("{site}", site)
          }
        />
        <DailyMoversPanel
          title={losersTitle}
          entries={movers?.losers ?? []}
          direction="down"
          teamMetaById={teamMetaById}
          emptyMessage={emptyMessage}
          teamLogoAlt={teamLogoAlt}
          profileLabel={strings.LEADERBOARD.VIEW_PLAYER_PROFILE}
          rankSiteLabel={(site) =>
            strings.LEADERBOARD.VIEW_PLAYER_RANK_SITE.replace("{site}", site)
          }
        />
      </div>
    </div>
  );
};

export const buildTeamMetaById = (
  teams: { teamId: string; teamName: string; fullName: string }[],
): Record<string, TeamMeta> =>
  Object.fromEntries(
    teams.map((team) => [
      team.teamId,
      {
        teamName: team.teamName,
        fullName: team.fullName || team.teamName,
        logoUrl: teamLogoUrl(team.teamId),
      },
    ]),
  );

export { normalizeMoversPayload } from "./hooks/useDailyMovers";
